import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import {
  useValidationTranslation,
  createContextFactory,
  useBooleanState,
  useIntl,
} from '@mfw/components';
import { isDefined, OrganizationUserType, UserStatusType } from '@mfw/common';
import { FirebaseService } from '@mfw/firebase/services';
import { captureException } from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { validationMessages } from '@mfw/translations';
import { FirebaseError } from '@mfw/firebase/app';
import React from 'react';

import type {
  LoginUserWithPasswordArgs,
  RegisterAccountResult,
  RegisterUserArgs,
} from '../api/types/user.types';
import type { CredentialTransition } from '../api/sessionCookie';
import type { UiAuthUser } from '../schemas/users.schema';
import type { UserCredential } from 'firebase/auth';
import type { Maybe } from '../types/maybe.types';

import type { SignOutReason } from '../api/http/signOutReason';

import { useResetSessionStores } from '../store/useResetSessionStores';
import { useSetMembershipCardBillingEnabled } from '../store/useMembershipCardBillingStore';
import {
  isErrorToastSuppressed,
  isQueryErrorToastSuppressed,
  shouldToastApiError,
  isSessionOverError,
} from './authErrorToast';
import {
  captureSignOutReasonFromTokenRead,
  beginSignOutReasonScope,
  clearSignOutReason,
  noteSignOutReason,
  getSignOutReason,
} from '../api/http/signOutReason';
import { signInReturnTargetFrom } from '../router/signInReturnTarget';
import { clearAccountCache } from './clearAccountCache';
import { setTenantOverride } from '../api/tenantOverride';
import { clearBrandingCache } from '../theme/brandingCache';
import { endSession } from '../api/users.api';
import { describeErrorForTag } from '../utils/describeErrorForTag';
import { isConnectivityError } from '../api/http/isConnectivityError';
import { isRefusedRenewalError } from '../api/http/isRefusedRenewalError';
import { SessionMovedError } from '../api/sessionCookie';
import { sessionCookie } from '../api/axios';
import { isPendingMemberInvite as computeIsPendingMemberInvite } from './isPendingMemberInvite';
import { useSessionExpiredNotice } from '../hooks/auth/useSessionExpiredNotice';
import { usersQueryKeys } from '../queryKeys/users.queryKeys';
import useCreateAccount from '../hooks/users/useCreateAccount';
import useLoggedUser from '../hooks/users/useLoggedUser';
import { useTenantContext } from './TenantProvider';
import { useSnackbar } from './SnackbarProvider';
import { useNavigate } from '../router/hooks';
import authMessages from './messages';

export type AuthUserUpdater =
  | ((prev: Maybe<UiAuthUser>) => Maybe<UiAuthUser>)
  | Maybe<UiAuthUser>;

/**
 * How a credential transition ended (§4.9).
 *
 * Five outcomes, because the page has five different things to say. `'write-failed'`
 * covers a definite refusal AND a transport blackout alike: neither proves the password
 * unchanged, so the page reports an unknown outcome and never repeats the PATCH by itself.
 * `'confirm-failed'` is the one that is merely transient — the credential IS rewritten and
 * the new Firebase identity is held, so the page retries through `refetchCurrentUser`.
 * `'not-started'` is the opposite end: the PATCH never left, so the typed password is not
 * spent and nothing about the account has moved.
 */
export type CredentialTransitionResult =
  | 'confirmed'
  | 'confirm-failed'
  | 'not-started'
  | 'sign-in-failed'
  | 'write-failed';

/**
 * WHO asked for the sign-out, and — when the app can tell — why.
 *
 * Omitted entirely by the five manual call sites (the settings dropdown, account deletion,
 * the two modal `onClose`s and the invite page): a user who pressed Logout is not owed an
 * explanation on the screen they are being taken to, and any note still pending belongs to
 * a session they have chosen to leave. `'terminal'` is the opposite case — nobody chose
 * it — and `reason` is whatever `getSignOutReason` could honestly make of the failure,
 * which for most terminal failures is `null`.
 */
export type SignOutOptions = {
  source: 'terminal';
  reason?: SignOutReason | null;
};

export type CredentialTransitionArgs = {
  /** the PATCH that rewrites the credential; it throws on a refusal */
  write: () => Promise<void>;
  newPassword: string;
  email: string;
};

export type AuthContext = {
  runCredentialTransition: (
    args: CredentialTransitionArgs,
  ) => Promise<CredentialTransitionResult>;
  registerWithPasswordAsync: (
    args: RegisterUserArgs,
  ) => Promise<RegisterAccountResult>;
  loginWithPasswordAsync: (args: LoginUserWithPasswordArgs) => Promise<boolean>;
  refetchCurrentUser: () => Promise<Maybe<UiAuthUser>>;
  setCurrentUser: (user: AuthUserUpdater) => void;
  /** true while a credential transition holds ordinary requests */
  isCredentialTransitionActive: boolean;
  loginWithGoogleAsync: () => Promise<void>;
  isClientOnboardingRequired: boolean;
  /** `member` onboarding finished but no org invite accepted yet — still
   * belongs on `/pending-invitation`, never redirected home. */
  isPendingMemberInvite: boolean;
  signOutAsync: (options?: SignOutOptions) => Promise<void>;
  currentUser: Maybe<UiAuthUser>;
  /**
   * A session this tab had published has ended, and nobody has signed in since. The route
   * guard reads it to tell somebody who arrived signed out from somebody who just left.
   */
  hasEndedSession: boolean;
  /** advances once at every successful explicit sign-in boundary */
  sessionRevision: number;
  isAuthenticated: boolean;
  isLoading: boolean;
};

/**
 * WHICH identity, in WHICH acting context, an asynchronous read belongs to.
 *
 * Captured before every token read and re-checked after every await. `(sub, auth_time)`
 * cannot do this job: `auth_time` has second precision, so an explicit re-sign-in of the
 * same account within the same second is indistinguishable from the sign-in it replaced —
 * and a super-admin can change acting tenant without the Firebase identity moving at all.
 * A counter this provider owns has neither problem.
 */
type IdentityAttempt = {
  tenantId: string | undefined;
  id: number;
};

/**
 * The explicit sign-in or credential transition that currently owns the login boundary.
 *
 * One at a time. While it is set, the `onIdTokenChanged` listener leaves its callbacks
 * alone — the operation is already reading that very token and will confirm it once.
 */
type ExplicitOperation = {
  /** releases the credential-transition hold, for the paths that cannot wait for it */
  endTransition?: () => void;
  isCredentialTransition: boolean;
};

/**
 * How long a boot renewal whose transport dropped IN FRONT of the user waits before its one
 * retry (Sentry 6Q, 15.09). Long enough to clear the blink that dropped the first request —
 * an instant retry lands in the same blink — and short enough to stay a spinner rather than
 * a stall. `online` ends the wait early.
 */
const VISIBLE_DROP_RETRY_DELAY_MS = 1500;

const Context = createContextFactory({
  devToolsName: 'AuthContext',
  initialValue: {} as AuthContext,
});

export interface AuthProviderProps {
  children: React.ReactNode;
}

const AuthProvider = ({ children }: AuthProviderProps) => {
  const { status: tenantStatus, tenant } = useTenantContext();
  const queryClient = useQueryClient();

  const [currentUser, setCurrentUser] =
    React.useState<Maybe<UiAuthUser>>(undefined);

  // 10.1 — mirrored into a store for the membership forms; signed out or a
  // server that does not send the field hides card billing (off by default)
  const setMembershipCardBillingEnabled = useSetMembershipCardBillingEnabled();
  const membershipCardBillingEnabled =
    currentUser?.membershipCardBillingEnabled === true;
  React.useEffect(() => {
    setMembershipCardBillingEnabled(membershipCardBillingEnabled);
  }, [membershipCardBillingEnabled, setMembershipCardBillingEnabled]);

  const endedSession = useBooleanState();
  // Through a ref: `handleSetCurrentUser` is in the listener effect's dependencies, and a
  // re-subscription of `onIdTokenChanged` is another boot renewal.
  const endedSessionRef = React.useRef(endedSession);
  endedSessionRef.current = endedSession;

  const [isInitializing, setIsInitializing] = React.useState(true);
  // Advances once per explicit sign-in boundary — password, Google, and the corp
  // credential transition. Ordinary token rollover and cookie recovery do NOT touch it:
  // the socket effect restarts on it, and an hourly rollover is not a new session.
  const [sessionRevision, setSessionRevision] = React.useState(0);
  // Never destructured: the socket effect reads it to stay closed for the whole window
  // in which this account's refresh tokens are revoked.
  const credentialTransition = useBooleanState();
  const { mutateAsync } = useLoggedUser();
  const { translate } = useValidationTranslation();
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const authRef = React.useRef(FirebaseService.getInstance().getFirebaseAuth());

  useSessionExpiredNotice();

  /**
   * The markers every asynchronous identity read is judged against.
   *
   * `identityAttempt` advances at each boundary that makes older work obsolete: an
   * explicit sign-in, a credential transition, a no-user callback, a logout, a tenant
   * move and effect cleanup. `tenantIdRef` follows the selected context synchronously,
   * because a tenant switch is a context change with no Firebase event at all.
   */
  const identityAttempt = React.useRef(0);
  const mountedRef = React.useRef(true);
  const tenantIdRef = React.useRef(tenant?.id);
  tenantIdRef.current = tenant?.id;
  /**
   * `currentUser`, readable WITHOUT waiting for a render.
   *
   * The rollover skip needs it: React state is a render behind, and a listener callback
   * that arrives in the same turn as the confirmation would otherwise see `undefined` and
   * mint a second time. Kept in step by `handleSetCurrentUser`, which is the only writer.
   */
  const currentUserRef = React.useRef<Maybe<UiAuthUser>>(undefined);
  /**
   * WHICH acting context the user in `currentUserRef` describes.
   *
   * A super-admin tenant switch changes no Firebase identity at all, so the same token
   * remains the confirmed login — and if a recovery confirms it for the NEW context before
   * this provider adopts anything, "we already have a user" would be true about the tenant
   * the app has just left (spec §3, §5 "Super-admin tenant switch"). Having a user is only
   * a reason to skip the mint when it is a user of the context asking.
   */
  const currentUserContextRef = React.useRef<string | undefined>(undefined);
  const explicitOperationRef = React.useRef<null | ExplicitOperation>(null);
  /**
   * Has this tab held — or begun holding — a session since the last cleanup?
   *
   * `onIdTokenChanged(null)` says two completely different things depending on the answer.
   * On a signed-out BOOT it is simply Firebase's first word: no account is here to drop,
   * and clearing the query cache would throw away the reads a public page has already
   * started — `TenantProvider`'s by-domain query among them, which this provider's own
   * effect waits for. After a session it is the sign-out itself, performed somewhere this
   * tab cannot see, and everything the account left behind has to go.
   *
   * React state cannot tell the two apart: a boot renewal still on the wire has no
   * `currentUser` yet, and it is exactly the case whose late continuation must not be
   * allowed to put the account back. So the marker is raised where an identity STARTS
   * being held — the listener's user branch and `claimExplicitOperation` — and lowered
   * by the cleanup that ends it.
   */
  const identityHeldRef = React.useRef(false);

  const captureAttempt = React.useCallback(
    (): IdentityAttempt => ({
      id: identityAttempt.current,
      tenantId: tenantIdRef.current,
    }),
    [],
  );
  const isAttemptCurrent = React.useCallback(
    (attempt: IdentityAttempt) =>
      mountedRef.current &&
      attempt.id === identityAttempt.current &&
      attempt.tenantId === tenantIdRef.current,
    [],
  );
  const assertAttempt = React.useCallback(
    (attempt: IdentityAttempt) => {
      if (!isAttemptCurrent(attempt)) throw new SessionMovedError();
    },
    [isAttemptCurrent],
  );

  // Accepts a plain value or a functional updater (resolved against the
  // freshest state) so callers can safely patch parts of the current user.
  // Either way the personal-trainer enrichment runs on the RESULT — with the
  // old value-only signature, updater functions slipped through untyped and
  // skipped the personalTrainerOrganizationId recompute entirely.
  //
  // Resolved against the REF rather than inside the state updater, so the answer exists
  // the instant this returns: `completeLogin` publishes the confirmed user and a listener
  // callback landing microseconds later has to be able to see it. The ref is written by
  // nothing else, so chaining two calls in one turn behaves exactly as a queued updater
  // would.
  const handleSetCurrentUser = React.useCallback((next: AuthUserUpdater) => {
    const previous = currentUserRef.current;
    const user = typeof next === 'function' ? next(previous) : next;
    const resolved = !user?.isPersonalTrainer
      ? user
      : {
          ...user,
          personalTrainerOrganizationId:
            user.organizationUsers && user.organizationUsers.length > 0
              ? user.organizationUsers[0].organizationId
              : undefined,
        };
    // A DIFFERENT account, or the same account in a different acting context, is a new
    // identity context — and the boundary the sign-out notice is scoped by. It is not
    // always reached through `claimExplicitOperation`: another tab signing in reaches this
    // tab through the listener alone, with no explicit operation anywhere. A rollover of
    // the identity already published is deliberately not one, or the hourly `/auth/me`
    // would silence a refusal that is being classified at that very moment.
    //
    // And the pair, in the same order as every other boundary (the manual logout,
    // `claimExplicitOperation`, the transition's confirmation): a note still sitting in the
    // slot belongs to a session this tab has now left. `beginSignOutReasonScope` only moves
    // OWNERSHIP forward, so without the clear the leftover survives — the case being an
    // incident whose tab never showed the login page, an SDK-only sign-out on the
    // guard-exempt `/auth/accept-invite` or on the public tree. A later terminal sign-out
    // that cannot name a reason writes nothing, and `LoginPage` would take that leftover
    // and tell a suspended or tenant-mismatched account that its credentials changed.
    const isNewIdentityContext =
      Boolean(resolved?.id) &&
      (resolved?.id !== previous?.id ||
        currentUserContextRef.current !== tenantIdRef.current);
    currentUserRef.current = resolved;
    currentUserContextRef.current = resolved ? tenantIdRef.current : undefined;
    if (isNewIdentityContext) {
      clearSignOutReason();
      beginSignOutReasonScope();
    }
    // Here, the one writer, so every way a session ends is covered and the flag lands in
    // the same commit as the user: a render with no user and the flag still down is what
    // the guard reads as somebody arriving signed out.
    if (resolved?.id) endedSessionRef.current.off();
    else if (previous?.id) endedSessionRef.current.on();
    setCurrentUser(resolved);
  }, []);

  const { mutateAsync: registerWithPassword } = useCreateAccount();
  const registerWithPasswordAsync = React.useCallback(
    async (args: RegisterUserArgs) => {
      const result = await registerWithPassword(args);
      // A deleted account exists: let the caller prompt for a decision instead
      // of navigating away.
      if (result?.kind === 'needsReactivationChoice') {
        return result;
      }
      navigate('/');
      return result;
    },
    [navigate, registerWithPassword],
  );

  // Every failing query and every failing mutation asks for a sign-out on its own, and an
  // ended session fails ALL of them at once. That was invisible while signing out was purely
  // local; now that it calls the backend, a burst of refused requests would be a burst of
  // logout requests. The first one runs, the rest wait for it.
  const signOutInFlight = React.useRef<null | Promise<void>>(null);
  const resetSessionStores = useResetSessionStores();

  /**
   * Runs one cleanup step, whatever it does to the ones after it.
   *
   * sen-8 U3: the sign-out promise is floated by five of its seven callers, so a throw from
   * any one step was an unhandled rejection AND skipped every step after it — an account
   * left in localStorage, a page never navigated away from. A failed step is reported the
   * sanitized way (a code, never the value) and the rest still run.
   */
  const runCleanupStep = React.useCallback((step: () => void) => {
    try {
      step();
    } catch (error) {
      captureException(
        new Error(
          'Local sign-out cleanup step failed; the remaining steps ran anyway',
        ),
        {
          tags: {
            signOutError: describeErrorForTag(error),
          },
        },
      );
    }
  }, []);

  /**
   * Everything this tab is holding on behalf of the account, dropped.
   *
   * ONE list, for the two ways a session ends: the sign-out this tab performs and the one
   * it only hears about (`onIdTokenChanged(null)` — another tab, a revoked credential,
   * storage cleared under the page). The second used to reopen the guard, drop
   * `currentUser` and stop there, so the query cache, the session atoms and the
   * super-admin tenant override all survived into whoever signed in next on this tab.
   *
   * `reset()` first, because it is the only thing that reopens the guard: it clears the
   * confirmed identity, the cooldown and the `quiesce()` latch that belonged to the session
   * being left.
   *
   * Deliberately NOT here: `POST /auth/logout`. The explicit sign-out sends it itself, in
   * front of all of this and behind a `quiesce()`, and it cannot move here — a cookie
   * belongs to the whole browser context, so a logout fired from a callback cannot be
   * ordered against another tab's newer mint, and no epoch of ours can retract a
   * `Set-Cookie` that has already been sent. The cookie an SDK-driven sign-out leaves
   * behind stays a documented limitation rather than a race introduced here.
   *
   * Navigation is not here either; see `signOutAsync`, which owns it.
   */
  const clearLocalSession = React.useCallback(() => {
    identityHeldRef.current = false;
    const steps: Array<() => void> = [
      () => sessionCookie.reset(),
      // The super-admin tenant switcher's override belongs to the SESSION being
      // ended, but it lives in localStorage — left behind, the next login on
      // this browser sends `X-Tenant-Id: <other tenant>` with every request, and
      // for anyone but a platform_super_admin the guard 403s everything,
      // `GET /auth/me` included, with no way back short of clearing storage by
      // hand.
      () => setTenantOverride(null),
      // The branding this device opens in was the account's organization; the
      // next account here must not boot into it (`theme/brandingCache`).
      () => clearBrandingCache(),
      // Same reason one layer in, for the atoms: nothing here reloads the page,
      // so every module-level store still holds what THIS account put there.
      // The hook owns the whole list — see it for what is deliberately kept.
      () => resetSessionStores(),
      // Through the setter, not `setCurrentUser`: the synchronous ref the rollover skip
      // reads has to be cleared with the state, or a listener callback arriving during
      // the sign-out still sees a user and skips the mint the next login needs.
      () => handleSetCurrentUser(undefined),
      () => clearAccountCache(queryClient),
    ];
    for (const step of steps) runCleanupStep(step);
  }, [handleSetCurrentUser, queryClient, resetSessionStores, runCleanupStep]);

  const signOutAsync = React.useCallback(
    async (options?: SignOutOptions) => {
      if (options?.source === 'terminal') {
        // At most one note per incident, and the FIRST one wins: the socket hook, a cache
        // subscriber and the SDK's own null callback can all be looking at the same refusal,
        // and one of them may have nothing to say about it. A call with no classified reason
        // must therefore never erase what the token read already captured — which is why
        // this is a note, not an assignment. `noteSignOutReason` owns that rule.
        if (options.reason) noteSignOutReason(options.reason);
      } else {
        // A user who pressed Logout gets no explanation, including one left pending by a
        // failure that was on its way when they pressed it. Before the flight, so a manual
        // logout that merely JOINS one already running still clears it — and before the new
        // scope, so the stragglers of the session being left cannot write a fresh one.
        clearSignOutReason();
        beginSignOutReasonScope();
      }
      if (signOutInFlight.current) return signOutInFlight.current;
      const running = (async () => {
        // Old identity work is obsolete from this line on: a token read or an `/auth/me`
        // still in flight belongs to the session being left, and `quiesce()` below waits
        // for it. Advancing the attempt is what keeps its continuation from publishing a
        // user, retrying, or reporting under the session that comes next.
        identityAttempt.current += 1;
        // A credential transition cannot be finished by a session that is ending, and
        // nothing may stay parked on its hold. `quiesce()` ends the guard's side of it;
        // this ends the provider's. Both are idempotent.
        explicitOperationRef.current?.endTransition?.();
        // FIRST, before anything is asked of the backend: close the guard and wait out the
        // renewal it may already have in flight. `POST /auth/logout` below bypasses the
        // barrier — it has to, it is the request that deletes the very cookie the barrier
        // would renew — so it neither joins that renewal nor waits for it. Left running, the
        // renewal answers AFTER the logout with a fresh `Set-Cookie`, the browser accepts it,
        // and the account is still signed in on the server while the app shows the login
        // screen. Bumping the epoch cannot undo that: it says what this tab may trust, not
        // what the browser is holding.
        await sessionCookie.quiesce();
        // Now, while a request can still be made on the cookie this is ending: ask the backend
        // to clear it. Everything below only ends the session in this tab; the cookie is the
        // server-side credential and it outlives all of it. A backend that cannot be reached
        // must not strand somebody in a session they asked to leave, so its failure is absorbed.
        await endSession().catch(() => undefined);
        try {
          await authRef.current.signOut();
        } catch (error) {
          // Swallowed on purpose. Seven call sites use `signOutAsync` and NONE has a `catch`
          // of its own: the settings dropdown and account deletion `await` it, while the
          // renewal refusal below, the two cache subscriptions and the two modal `onClose`s
          // (OTP confirmation, privacy policy) float it outright (sen-8 U3). Not one of them
          // could do anything with the failure: the backend session is already gone and the
          // user has asked to leave. Rethrowing only turned it into an unhandled rejection
          // and skipped everything below.
          //
          // Not silent, though. Firebase could not forget the user, so the account stays
          // signed in on this device until something clears it, and that is worth knowing
          // about. Sanitized the way every other reporter in this file is: the rejected value
          // can carry credentials, so only a CODE travels — never the message or the value.
          captureException(
            new Error(
              'Firebase sign-out failed; the session was ended locally anyway',
            ),
            {
              tags: {
                signOutError: describeErrorForTag(error),
              },
            },
          );
        }
        // Read before the cleanup below clears it. A refusal nobody chose, of a session this
        // tab never showed, is still somebody opening a link. See the navigation at the end.
        // The flag matters as much as the user: a request refused after a logout finished also
        // finds no user, and the page it finds is the one that person left.
        const isRefusedBeforeAnyUser =
          options?.source === 'terminal' &&
          !currentUserRef.current?.id &&
          !endedSessionRef.current.isActive;
        // EVERYTHING below runs whether or not Firebase let go, because `POST /auth/logout`
        // above has already deleted the server-side session. Leaving the local half standing
        // meant the user sat on a protected page with the app still believing it was signed
        // in, and — since `COOKIE_REQUIRED` is deliberately never toasted — Logout looked
        // like it had done nothing at all.
        clearLocalSession();
        // The one step of the tail that is NOT shared with the listener's no-user branch.
        // Sending the user to the login screen is right when the user asked to leave; done
        // for a sign-out that happened somewhere else it would turn every public and auth
        // route — `/p/<slug>`, the marketplace teaser, the login page itself — into an
        // unconditional redirect. The route guards already decide what a signed-out tab may
        // render, and they do it without moving anybody who is allowed to stay.
        //
        // A session refused on boot takes the deep link it opened along, in the two orderings
        // of the guard's own redirect. If that has landed, the link is in this entry's history
        // state and a second `/auth/login` entry would replace it with nothing, so there is
        // nothing to do. If it is still waiting for the lazy login chunk, this navigation
        // interrupts it and has to carry the link itself.
        if (window.location.pathname !== '/auth/login') {
          runCleanupStep(() =>
            isRefusedBeforeAnyUser
              ? navigate('/auth/login', {
                  state: signInReturnTargetFrom(window.location),
                })
              : navigate('/auth/login'),
          );
        }
      })();
      signOutInFlight.current = running;
      try {
        await running;
      } finally {
        signOutInFlight.current = null;
      }
    },
    [clearLocalSession, navigate, runCleanupStep],
  );

  // Read through a ref by everything that must not re-subscribe `onIdTokenChanged` when
  // `signOutAsync`'s identity changes — a re-subscription is another boot renewal.
  const signOutAsyncRef = React.useRef(signOutAsync);
  signOutAsyncRef.current = signOutAsync;
  // Same reason, same shape: the listener effect must not re-subscribe `onIdTokenChanged`
  // because a cleanup callback's identity moved.
  const clearLocalSessionRef = React.useRef(clearLocalSession);
  clearLocalSessionRef.current = clearLocalSession;

  const refetchCurrentUser = React.useCallback(async () => {
    // A credential transition holds ordinary work for exactly this reason: the backend
    // revoked this account's refresh tokens BEFORE answering the password write, so the
    // forced read below would be refused — and the Firebase SDK answers a refused refresh
    // by signing the user out locally, before the re-sign-in that ends the transition.
    // The transition confirms the user itself; there is nothing here to wait for.
    if (explicitOperationRef.current?.isCredentialTransition) return undefined;
    const attempt = captureAttempt();
    return new Promise<Maybe<UiAuthUser>>((resolve) => {
      const user = authRef.current.currentUser;
      if (!user) {
        resolve(undefined);
        return;
      }
      // Through the shared wrapper, like every other session token read in the app: on
      // `auth/user-token-expired` the SDK signs the user out from inside this very call
      // and only then rejects, so the `catch` below runs on a tab that has already been
      // navigated away. The wrapper classifies around the read instead, and the error
      // reaches that `catch` exactly as it was thrown.
      captureSignOutReasonFromTokenRead(() =>
        user.getIdToken(/** force refresh */ true),
      )
        .then((token) => {
          // The account or the acting tenant moved while that read was on the wire. What
          // comes back describes a session this tab has already left, and `undefined` is
          // the answer every caller here already handles.
          if (!isAttemptCurrent(attempt)) {
            resolve(undefined);
            return;
          }
          // Every caller of this just CHANGED the account, and the answer becomes
          // `currentUser`: a `/auth/me` that was already in flight before the change is
          // no answer at all. The listener below does not say this, and must not — its
          // renewal follows the guard's own forced refresh and changed nothing.
          mutateAsync({ afterMutation: true, token })
            .then((user) => {
              if (!isAttemptCurrent(attempt)) {
                resolve(undefined);
                return;
              }
              handleSetCurrentUser(user);
              // The memberships just moved with the account (an accepted invite, a new
              // profile, onboarding), and every cross-organization fan-out reads them
              // together with the lock state in the cached logical profiles
              // (`useCrossOrganizationReadableMemberships`): an organization missing from
              // that list is skipped. Refreshed here, with the memberships — nothing else
              // would until the cache goes stale, and `refetchOnWindowFocus` is off
              // app-wide.
              void queryClient.invalidateQueries({
                queryKey: usersQueryKeys.fetchLogicalProfiles().queryKey,
              });
              resolve(user);
            })
            .catch(() => {
              resolve(undefined);
            });
        })
        // `getIdToken(true)` ALWAYS goes to the network — Firebase does not serve a forced
        // refresh from cache — so any transport blip rejects it with
        // `auth/network-request-failed`. `resolve` lives only in the `then` above, so that
        // rejection used to leave this promise pending forever, and React Query awaits a
        // mutation's `onSuccess`: the org switch that called this never finished, having
        // already cleared the organization cache before asking. Answering `undefined` is
        // the same answer a signed-out account gets, and every caller already handles it.
        //
        // Sanitized like the listener's below: the rejected value can be an AxiosError
        // carrying the Bearer token, so only a CODE travels. Not the NAME: Firebase stamps
        // one constant on every code it mints, so the name could not tell the blip this
        // comment names apart from a session that is really over.
        //
        // The blip is a warning: the caller survives it by design (MYFITWORLD-V2-6Q).
        .catch((error: unknown) => {
          captureException(
            new Error(
              'Forced ID-token refresh failed; current user not renewed',
            ),
            {
              level: isConnectivityError(error) ? 'warning' : 'error',
              tags: {
                refreshError: describeErrorForTag(error),
              },
            },
          );
          resolve(undefined);
        });
    });
  }, [
    captureAttempt,
    isAttemptCurrent,
    mutateAsync,
    handleSetCurrentUser,
    queryClient,
  ]);

  /**
   * Is this failure one to RETRY rather than answer?
   *
   * The failure has to be a dropped transport, and a renewal talks to TWO hosts, so the
   * drop has two shapes — `NetworkError` is our own `/auth/me` that went out and got
   * nothing back, `auth/network-request-failed` is the token refresh that could not reach
   * Google's endpoint (`getIdToken()` serves the cache only outside the last 30s of the
   * token's hour; past that it goes to the wire, and a backgrounded tab kills it the same
   * way). `isConnectivityError` is the one predicate for both, shared with the session
   * guard's `isConnectivityFault` so the two hops can never again answer differently.
   * Neither shape says anything about the session either way — and Firebase has to still
   * hold the account, because that is the session being waited for.
   *
   * Visibility is deliberately NOT a condition any more. It decides WHEN the retry runs
   * (see the failure branch of `renewCurrentUser`), not WHETHER: the 15.09 6Q event was a
   * page that never left the foreground, whose `/auth/me` Cloud Run served with 200 while
   * the phone recorded status 0, and it still ended at `/auth/login` with the session
   * alive.
   */
  const shouldRetryDroppedRenewal = React.useCallback(
    (error: unknown) =>
      isConnectivityError(error) && Boolean(authRef.current.currentUser),
    [],
  );

  /**
   * The ONE boundary at which this tab starts a new session (spec §3).
   *
   * Password, Google and the corp credential transition all arrive here, and they all
   * arrive with the credential Firebase just returned — not with `authRef.current.currentUser`,
   * which the listener may already have moved. Everything that makes the login a NEW one
   * happens in this function and nowhere else: `reset()` clears the confirmation the old
   * login left behind (including its cooldown, so a same-second re-sign-in is not refused
   * for thirty seconds by the failure of the login it replaces), and the revision advances
   * once so the socket effect restarts exactly once.
   *
   * The two checks after the awaits are not the same question. The attempt answers "is
   * this still the identity work the app wants"; `isCurrent(stamp)` answers "has the guard
   * moved underneath us" — a logout or a tenant switch that never touched this provider.
   */
  const completeLogin = React.useCallback(
    async (
      user: { getIdToken: () => Promise<string> },
      attempt: IdentityAttempt,
    ) => {
      assertAttempt(attempt);
      // The GUARD's boundary stays here, in front of the awaits: `reset()` clears the
      // confirmation and the cooldown the old login left behind, and the mint below has to
      // run on a clean one. The React revision does NOT belong here — see below.
      sessionCookie.reset();
      const stamp = sessionCookie.getStamp();
      const assertCurrent = () => {
        assertAttempt(attempt);
        if (!sessionCookie.isCurrent(stamp)) throw new SessionMovedError();
      };
      const token = await captureSignOutReasonFromTokenRead(() =>
        user.getIdToken(),
      );
      assertCurrent();
      const me = await mutateAsync({ afterMutation: true, token });
      assertCurrent();
      // Synchronous, so the listener callback for this very sign-in — which may land in
      // the same turn — sees the user and skips its duplicate mint.
      handleSetCurrentUser(me);
      // In the SAME commit as the user, and that is the whole point of it being here
      // rather than in front of the two awaits above. Both awaits flush a commit, so a
      // revision advanced early is published while `currentUser` is still the old value —
      // and every consumer keyed on the pair (the account scope is `id:tenant:revision`)
      // then sees TWO account boundaries for one login: `anonymous:<tenant>:n+1` first,
      // the real account second. Each boundary costs an organization cache sweep and an
      // upload-worker rebuild, and the anonymous half is the accept-invite login this PR
      // exists for. React batches these two setters into one commit, so the intermediate
      // pair never exists.
      setSessionRevision((revision) => revision + 1);
      // The listener is suppressed for the whole operation, so it cannot finish
      // initialization on our behalf.
      setIsInitializing(false);
    },
    [assertAttempt, handleSetCurrentUser, mutateAsync],
  );

  /**
   * A sign-in that Firebase itself refused.
   *
   * A dropped connection is not a wrong password. Announcing both as invalid credentials
   * sent the user looking for a typo in a credential the server never rejected — and on
   * the corp transition it would have been said about a password that WAS accepted.
   */
  const announceFailedSignIn = React.useCallback(
    (error: unknown) => {
      enqueueSnackbar({
        message: formatMessage(
          isConnectivityError(error)
            ? authMessages.signInNotCompleted
            : validationMessages.VALIDATION__InvalidCredentials,
        ),
        variant: 'error',
      });
    },
    [enqueueSnackbar, formatMessage],
  );

  /**
   * A sign-in Firebase accepted whose CONFIRMATION did not arrive.
   *
   * Three outcomes, one of which does nothing at all. `SessionMovedError` means a logout,
   * a tenant switch or a newer sign-in invalidated this one while it ran: whatever did
   * that owns the screen now, so a toast here would describe a session nobody is in.
   * A terminal refusal ends the session through the existing single flight — the cache
   * subscriber may be observing the same refusal, and it must still produce one sign-out.
   * Everything else keeps the Firebase identity: the mutation cache has already toasted
   * it, and the page can ask again.
   */
  const settleFailedConfirmation = React.useCallback((error: unknown) => {
    if (error instanceof SessionMovedError) return;
    if (!isSessionOverError(error)) return;
    // Floated on purpose: `signOutAsync` never rejects (sen-8 U3).
    signOutAsyncRef.current({
      source: 'terminal',
      reason: getSignOutReason(error),
    });
    // A warning, for the same reason the renewal's own sign-out is one: this is the
    // design working, and the user is on the login screen where one sign-in fixes it.
    // Sanitized like every other reporter here — the rejected value can be an AxiosError
    // carrying the Bearer token, so only a CODE travels.
    captureException(
      new Error('Sign-in could not be confirmed; the session was refused'),
      {
        level: 'warning',
        tags: {
          loginError: describeErrorForTag(error),
        },
      },
    );
  }, []);

  /**
   * Firebase password sign-in, with the legacy fallback to the default tenant.
   *
   * Returns the CREDENTIAL rather than a boolean: the caller has to confirm with the user
   * this call signed in, and re-reading `authRef.current.currentUser` afterwards is a
   * different question with a different answer while a listener is running.
   */
  const signInWithPassword = React.useCallback(
    async ({
      email,
      password,
    }: LoginUserWithPasswordArgs): Promise<UserCredential> => {
      return signInWithEmailAndPassword(authRef.current, email, password);
    },
    [],
  );

  /**
   * Claims the login boundary for one explicit operation, or answers that it is taken.
   *
   * A second sign-in while one is running would race it for the guard epoch and the
   * revision, and the loser would publish a user for a session the winner has replaced.
   * The one already in flight keeps it.
   */
  const claimExplicitOperation = React.useCallback(
    (isCredentialTransition: boolean): ExplicitOperation | null => {
      if (explicitOperationRef.current) return null;
      const operation: ExplicitOperation = { isCredentialTransition };
      explicitOperationRef.current = operation;
      // A person typing a password is not owed a notice about the session before it, and
      // a token read still in flight from that session may not describe this one. Same
      // pair, and the same order, as the manual logout above.
      clearSignOutReason();
      beginSignOutReasonScope();
      // A login in flight is a session this tab is already holding, even though React state
      // is still anonymous: a no-user callback landing now ends a session, not a boot.
      identityHeldRef.current = true;
      identityAttempt.current += 1;
      return operation;
    },
    [],
  );

  const releaseExplicitOperation = React.useCallback(
    (operation: ExplicitOperation) => {
      // Only if this call is still the owner: a logout or the no-user branch may have
      // handed the boundary on, and clearing it then would unlock the operation that is
      // actually running.
      if (explicitOperationRef.current === operation) {
        explicitOperationRef.current = null;
      }
    },
    [],
  );

  const loginWithPasswordAsync = React.useCallback(
    async (args: LoginUserWithPasswordArgs) => {
      const operation = claimExplicitOperation(false);
      if (!operation) return false;
      const attempt = captureAttempt();
      try {
        let credential: UserCredential;
        try {
          credential = await signInWithPassword(args);
        } catch (error) {
          announceFailedSignIn(error);
          return false;
        }
        await completeLogin(credential.user, attempt);
        // `true` only now: the guarded `/auth/me` has answered and `currentUser` is set.
        // The old boolean meant "Firebase accepted the password", which the corp page
        // then read as proof of a session it did not yet have.
        return true;
      } catch (error) {
        settleFailedConfirmation(error);
        return false;
      } finally {
        releaseExplicitOperation(operation);
        // However it ended, the app is no longer waiting to find out who is signed in.
        setIsInitializing(false);
      }
    },
    [
      announceFailedSignIn,
      captureAttempt,
      claimExplicitOperation,
      completeLogin,
      releaseExplicitOperation,
      settleFailedConfirmation,
      signInWithPassword,
    ],
  );

  const loginWithGoogleAsync = React.useCallback(async () => {
    const operation = claimExplicitOperation(false);
    if (!operation) return;
    const attempt = captureAttempt();
    try {
      let credential: UserCredential;
      try {
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({
          prompt: 'select_account',
        });

        credential = await signInWithPopup(authRef.current, provider);
      } catch (error) {
        // Unchanged: only a Firebase refusal is announced. A closed popup rejects with
        // something else entirely, and the user closing it is not news.
        if (error instanceof FirebaseError) announceFailedSignIn(error);
        return;
      }
      await completeLogin(credential.user, attempt);
    } catch (error) {
      settleFailedConfirmation(error);
    } finally {
      releaseExplicitOperation(operation);
      setIsInitializing(false);
    }
  }, [
    announceFailedSignIn,
    captureAttempt,
    claimExplicitOperation,
    completeLogin,
    releaseExplicitOperation,
    settleFailedConfirmation,
  ]);

  /**
   * The corp password change, as ONE coordinated operation (spec §4.9).
   *
   * The backend revokes this account's Firebase refresh tokens, stamps the revocation
   * cutoff and drops its sockets BEFORE it answers the PATCH. So between that answer and
   * the new sign-in, every forced token refresh is refused — and the SDK answers a refused
   * refresh by signing the user out locally. The hold is what keeps anything from going
   * looking for a token in that window; it is not `quiesce()`, which would block the very
   * mint that ends the transition.
   *
   * There is no watchdog on the hold, deliberately: every exit below runs the `finally`,
   * and the two events that can strand it — a logout and a no-user callback — end it
   * themselves.
   */
  const runCredentialTransition = React.useCallback(
    async ({
      email,
      newPassword,
      write,
    }: CredentialTransitionArgs): Promise<CredentialTransitionResult> => {
      const operation = claimExplicitOperation(true);
      // No second PATCH, and no explicit sign-in racing the one already running. Nothing
      // was written on this path, so it is not a failed sign-in — it is a transition that
      // never began.
      if (!operation) return 'not-started';
      const attempt = captureAttempt();
      credentialTransition.on();
      let transition: CredentialTransition | null = null;
      operation.endTransition = () => transition?.end();
      try {
        transition = await sessionCookie.beginCredentialTransition();
        try {
          await write();
        } catch {
          // A definite refusal and a transport blackout end the same way here: neither
          // proves the password unchanged, so the PATCH is never repeated automatically.
          // The page keeps the mutation's own field errors for the refusal it can name.
          return 'write-failed';
        }
        // A logout, or a no-user callback from another tab, landed while the PATCH was on
        // the wire. It cannot undo the write — the credential IS rewritten — but signing
        // in here would hand Firebase a fresh identity to PERSIST on a device that has
        // just left the session, and the app would then show a signed-out screen over a
        // live account. `'confirm-failed'` is the honest outcome: the password changed and
        // this tab could not confirm a session for it.
        if (!isAttemptCurrent(attempt)) return 'confirm-failed';
        let credential: UserCredential;
        try {
          // No default-tenant fallback: this account is signed in, on its own tenant.
          credential = await signInWithEmailAndPassword(
            authRef.current,
            email,
            newPassword,
          );
        } catch {
          return 'sign-in-failed';
        }
        try {
          await completeLogin(credential.user, attempt);
        } catch (error) {
          // The outcome is already decided here — the credential IS rewritten — and
          // REPORTING the failure must not be able to change it. `settleFailedConfirmation`
          // signs out, captures and announces; a throw out of any of that would otherwise
          // land in the outer `catch` below and be told to the user as "nothing was
          // changed", over a password that has in fact moved.
          try {
            settleFailedConfirmation(error);
          } catch {
            // Swallowed on purpose: there is nothing left to say that the outcome below
            // does not already say.
          }
          return 'confirm-failed';
        }
        // Belt and braces, and it costs one call: the window this transition opened is a
        // window in which every token read of this account is refused, and a note taken
        // anywhere in it describes THIS transition rather than a session that ended.
        // Confirming republishes the same id and tenant, which `handleSetCurrentUser` does
        // not count as a new identity context, so nothing else would drop it. The user is
        // signed in on the credential they just chose; there is nothing to explain.
        //
        // The clear alone is not enough: it drops the note but leaves incident ownership
        // exactly where it was, so a later REAL refusal in this same session would find
        // `notedScope === scope` and be silenced by `noteSignOutReason`'s own dedupe — the
        // very incident the notice exists for. Pairing the clear with
        // `beginSignOutReasonScope`, the same pair and the same order as every other
        // boundary (the manual logout, `claimExplicitOperation`, a new identity or acting
        // tenant), advances ownership past this confirmation: the explicit sign-in that
        // just completed is itself a boundary, and it also makes a straggler read from the
        // window publish nothing by OWNERSHIP rather than by ordering.
        clearSignOutReason();
        beginSignOutReasonScope();
        // Only now: `handleSetCurrentUser` has run and `mustChangePassword` is cleared.
        return 'confirmed';
      } catch {
        // `beginCredentialTransition()` is the only statement left in this `try` that can
        // throw into here: the write, the sign-in and the confirmation each answer with an
        // outcome of their own, and the confirmation's failure REPORT is caught where it
        // is raised precisely so it cannot reach this line. So this is exactly "the hold
        // was refused": nothing was written, nothing to repeat, and the password the user
        // typed is still theirs.
        return 'not-started';
      } finally {
        transition?.end();
        credentialTransition.off();
        releaseExplicitOperation(operation);
      }
    },
    [
      captureAttempt,
      claimExplicitOperation,
      completeLogin,
      credentialTransition,
      isAttemptCurrent,
      releaseExplicitOperation,
      settleFailedConfirmation,
    ],
  );

  React.useEffect(() => {
    // The acting tenant is part of a renewal's identity: the guard stamps `/auth/me` with
    // it and refuses an answer that arrives under a different one. `TenantProvider` renders
    // this subtree while its by-domain query is still in flight, so starting here would
    // stamp the boot renewal `''` — "no tenant resolved yet" — and the tenant landing a
    // moment later would invalidate the very response the app is waiting for. Initialization
    // stays open (a spinner, not a signed-out screen) until the tenant question is settled,
    // either way: `error` is an answer too, and gating on the tenant VALUE instead would
    // hold every account hostage to a tenant that may never load.
    if (tenantStatus === 'pending') return;
    /**
     * WHICH acting tenant this run of the effect belongs to.
     *
     * Compared against `tenantIdRef` in the cleanup, which React has already updated to the
     * NEXT value by then (it is written during render, and the re-render precedes the
     * teardown). The two differ only on a real move of the acting tenant — which is the
     * only re-run of this effect that is also a change of identity context.
     */
    const effectTenantId = tenant?.id;
    // The tenant of THIS domain or none. One image serves every domain, so a tenant named by
    // the build could be another customer's.
    authRef.current.tenantId = tenant?.id ?? null;
    // Restored on every setup, not only on the first: React StrictMode mounts, cleans up
    // and mounts again, and the second mount would otherwise start with the flag the
    // first one's cleanup left behind — every read below rejected as obsolete.
    mountedRef.current = true;

    /**
     * Has this effect's cleanup already run?
     *
     * Cleanup releases the hold ARMED at that moment, but a renewal still in flight
     * outlives it — and its rejection handler would otherwise arm fresh listeners on this
     * dead closure, firing `/auth/me` for a tree that no longer exists and reporting
     * "initialized signed out" for an app instance that is already gone. Every async
     * continuation below checks this before touching state, retrying, or arming a hold.
     */
    let disposed = false;

    /**
     * Releases the armed return-listeners, or `null` when nothing is held.
     *
     * At most one hold at a time: the renewal it waits for is the only one running, and a
     * second pair of listeners would fire a second `/auth/me` for the same return.
     */
    let releaseHold: null | (() => void) = null;

    /**
     * Was the page hidden at any point since the running renewal started?
     *
     * Seeded from `document.hidden` when a renewal begins and raised by the listener below,
     * so it covers the whole flight rather than the instant the rejection happens to be
     * delivered. Reading `document.hidden` at that instant is a bet on iOS timing: Safari
     * may run the abort handler while the tab is still hidden — what the 6Q trace shows,
     * two Sentry events sent 70ms after the hide — or freeze the page first and deliver
     * the rejection only on resume, when `document.hidden` is already `false`. The whole
     * flight answers both, and it decides between retrying NOW (the user is back already)
     * and retrying after a pause (the page never left the foreground).
     */
    let hiddenWhileInFlight = false;
    const noteHidden = () => {
      if (document.hidden) hiddenWhileInFlight = true;
    };
    document.addEventListener('visibilitychange', noteHidden);

    /**
     * Arms the ONE retry of a dropped renewal and releases it on the first of: the page
     * coming back, the browser going `online`, or — when `pauseMs` is given — the pause
     * running out.
     *
     * Without a pause the hold is armed while the page is HIDDEN, so `visibilitychange` on
     * the way back is the release event. `online` is a second chance at the same instant,
     * not a second scenario: `document.hidden` flips before `visibilitychange` is
     * delivered, so on a wake-up where connectivity returns at the same moment a queued
     * `online` can run first and be the event that releases the hold.
     *
     * With a pause it is armed for a page that stayed VISIBLE and merely lost the
     * connection (6Q, 15.09): the timer is the usual release, `online` the early one. A
     * pause that runs out while the page is hidden leaves the hold armed, and coming back
     * releases it, so the retry never runs in the background where it would drop again.
     */
    const holdForReturn = (retry: () => void, pauseMs?: number) => {
      if (releaseHold) return;
      const onReturn = () => {
        // `visibilitychange` fires on the way OUT too, and that is the state we are already
        // waiting in. Only coming BACK is the event that can answer anything.
        if (document.hidden) return;
        releaseHold?.();
        retry();
      };
      document.addEventListener('visibilitychange', onReturn);
      window.addEventListener('online', onReturn);
      const pause = isDefined(pauseMs)
        ? window.setTimeout(onReturn, pauseMs)
        : undefined;
      releaseHold = () => {
        document.removeEventListener('visibilitychange', onReturn);
        window.removeEventListener('online', onReturn);
        window.clearTimeout(pause);
        releaseHold = null;
      };
    };

    const renewCurrentUser = (
      user: { getIdToken: () => Promise<string> },
      isRetry = false,
    ): void => {
      // BEFORE the read, not after it. An explicit sign-in or a credential transition owns
      // this callback either way — it is holding the very token this would re-read and it
      // confirms it exactly once — but inside a CREDENTIAL TRANSITION the read is worse
      // than redundant: the backend revoked this account's refresh tokens before it
      // answered the write, so the read is refused, and the wrapper dutifully notes
      // `'credentials-changed'` for the account's own transition. `completeLogin` then
      // republishes the same id and the same acting tenant — not a new identity context —
      // so nothing clears that note and it surfaces on the next `/auth/login`, over a
      // session that is perfectly healthy. Not reading is the whole fix; the same check
      // stays below for an operation that claims the boundary while a read is in flight.
      if (explicitOperationRef.current) return;
      hiddenWhileInFlight = document.hidden;
      // Captured BEFORE the read and re-checked after it, after `/auth/me`, and in every
      // continuation below. Cleanup alone cannot do this job: a no-user callback or a
      // newer login happens while this same effect stays mounted.
      const attempt = captureAttempt();
      captureSignOutReasonFromTokenRead(() => user.getIdToken())
        .then((token) => {
          // Firebase's token read can outlive cleanup, including on a retry.
          if (disposed) return 'stop' as const;
          assertAttempt(attempt);
          // An explicit sign-in or a credential transition owns this callback: it is
          // already holding the very token below and confirms it exactly once. Finishing
          // initialization here would render the app signed out mid-login, which is the
          // login screen the corp page used to navigate away from too early.
          if (explicitOperationRef.current) return 'stop' as const;
          // An hourly rollover of a login THIS context has already adopted. The key on
          // its own is not enough: another consumer may have minted the cookie before
          // this provider ever had a user — on boot, or for the tenant a super-admin has
          // just switched to — and that initialization still has to fetch one (§3).
          if (
            currentUserRef.current &&
            currentUserContextRef.current === tenantIdRef.current &&
            sessionCookie.isSameLogin(token)
          ) {
            return 'settle' as const;
          }
          return mutateAsync({ afterMutation: false, token }).then((me) => {
            if (disposed) return 'stop' as const;
            assertAttempt(attempt);
            handleSetCurrentUser(me);
            return 'settle' as const;
          });
        })
        // The renewal REJECTS when a sign-out or a tenant switch lands while `/auth/me`
        // is in flight — the answer would describe a session the app has already left,
        // so the guard refuses to hand it back. There is nothing to set in that case,
        // and both paths re-render on their own. Without this catch the rejection
        // escaped as an uncaught runtime error on every logout.
        //
        // Everything ELSE that can reject here — a 5xx, a renewal cooldown, a bug in
        // this very chain — ends up looking the same on screen (initialization finishes
        // with no user, so the app renders signed out while Firebase still holds a
        // session) but is not expected at all, and was swallowed without a trace. It
        // gets one report, sanitized the way the guard's own reporter is: the rejected
        // value can be an AxiosError carrying the Bearer token, so only a code and a
        // status travel. A NAME could not do it: `BaseApiResponseError` does not extend
        // `Error`, so every refusal here used to be reported as the word `object`.
        .then(
          (verdict) => {
            // `'stop'` is a callback that was SUPPRESSED, not one that answered: the
            // operation it belongs to publishes the user and finishes initialization
            // itself, and doing either here would do it a render too early.
            if (disposed || verdict === 'stop') return;
            setIsInitializing(false);
          },
          (error: unknown) => {
            if (disposed) return;
            // An obsolete attempt is silent all the way down — no publication, no retry,
            // no report, no sign-out. Everything that can invalidate one (a no-user
            // callback, a logout, an explicit login, a tenant move) settles
            // initialization itself, so this cannot leave a spinner standing.
            if (!isAttemptCurrent(attempt)) return;
            if (error instanceof SessionMovedError) {
              setIsInitializing(false);
              return;
            }
            // Sentry 6Q: a request that got no answer is not an answer. Finishing here
            // renders the app signed out — `AuthenticatedGuard` redirects to
            // `/auth/login`, `replace`, so the route the user was opening is gone too —
            // on the strength of a transport that dropped while Firebase still holds the
            // session and the backend is serving `/auth/me` with 200.
            //
            // Exactly ONE extra attempt — `isRetry` is the bound, and it does not depend on
            // visibility, so neither an unbounded spinner nor an endless retry loop is
            // reachable. Where that attempt happens does depend on visibility:
            //
            //   still hidden  → hold, and let coming back run it. Waiting costs nothing
            //                   while nobody is looking, and the return event is guaranteed.
            //   already back  → run it NOW. The page was hidden in flight and the user has
            //                   returned already, so the event that would release a hold has
            //                   been and gone; holding here would wait forever.
            //   never hidden  → pause, then run it (6Q, 15.09). The connection blinked with
            //                   the user watching; an instant retry would land in the same
            //                   blink, and `online` runs it early when the browser noticed.
            if (!isRetry && shouldRetryDroppedRenewal(error)) {
              const retry = () => renewCurrentUser(user, true);
              if (document.hidden) {
                holdForReturn(retry);
              } else if (hiddenWhileInFlight) {
                retry();
              } else {
                holdForReturn(retry, VISIBLE_DROP_RETRY_DELAY_MS);
              }
              return;
            }
            // A refused IDENTITY is final for this renewal (no replay), and it will be
            // refused again on the next boot — den-5 §2: the dev tenant cutover left every
            // persisted session bouncing on `/auth/login` with two toasts per boot. Drop it,
            // so the next boot starts clean instead of repeating the refusal. Only the two
            // identity codes qualify; `INVALID_TOKEN` is a verification failure that is far
            // more often the backend's key cache or clock than the identity, and this boot
            // simply ends signed out with the identity kept for the next one (sen-6 S1).
            const refused = isRefusedRenewalError(error);
            if (refused) {
              // Floated on purpose: `signOutAsync` never rejects (sen-8 U3).
              signOutAsyncRef.current({
                source: 'terminal',
                reason: getSignOutReason(error),
              });
            }
            // Still reported — a population of these arriving at once is worth seeing —
            // but a refused identity is a WARNING, because the two lines above are the
            // design working: the session was ended because the backend said this identity
            // does not belong here, and the user is on the login screen where one sign-in
            // fixes it. Filed as an error it made a correct sign-out look like a fault,
            // and 6Q reopened every time a browser left over from the tenant cutover
            // finally cleaned itself up. Everything else keeps `error`: nobody chose it.
            captureException(
              new Error(
                'Current-user renewal failed; the app initialized signed out',
              ),
              {
                level: refused ? 'warning' : 'error',
                tags: {
                  renewalError: describeErrorForTag(error),
                },
              },
            );
            setIsInitializing(false);
          },
        );
    };

    // `onIdTokenChanged` returns its unsubscribe — dropping it added another listener on
    // every re-run of this effect, and with it a duplicate `/auth/me` per token rollover.
    const unsubscribe = authRef.current.onIdTokenChanged((user) => {
      if (user) {
        // From here until the cleanup, a no-user callback means a SESSION ENDED rather than
        // a signed-out boot — including while this very renewal is still on the wire.
        identityHeldRef.current = true;
        // A token rollover while a hold is armed makes that hold obsolete: this renewal
        // carries the newer token, and the held one would replay the older.
        releaseHold?.();
        renewCurrentUser(user);
      } else {
        // Every sign-out this tab did not perform itself — another tab, revoked
        // credentials, cleared storage — reaches it ONLY here. Clearing React state is not
        // enough: the guard would keep the ended session's confirmed identity, epoch and
        // cooldown, so the next request fails closed and writes a cooldown keyed to that
        // same account — and the next LOGIN of that account is then refused for the length
        // of the cooldown, silently, because the `catch` above swallows the rejection.
        // A hold waits for a session Firebase has just said is gone. Releasing it here is
        // what keeps a signed-out tab from firing `/auth/me` on its next foreground.
        //
        // This branch runs even while an explicit sign-in is pending — a logout in
        // another tab does not wait for one — so it invalidates old identity work first:
        // a token read or an `/auth/me` still in flight belongs to the session that just
        // ended, and its continuation must not resurrect a user from it.
        identityAttempt.current += 1;
        // A credential transition cannot be completed by a session that no longer exists.
        // It cannot undo the server-side write either, so all this does is let everything
        // parked on the hold go; the transition's own `finally` still runs. Idempotent.
        explicitOperationRef.current?.endTransition?.();
        releaseHold?.();
        // The cleanup the explicit sign-out runs, minus its navigation: the query cache,
        // the session atoms and the tenant override belong to the session that just ended,
        // and nothing here reloads the page to drop them on its own. Two callers reach this
        // callback with nothing to clean, and each needs its own marker:
        //
        //   a signed-out BOOT — Firebase's first word, no account here to drop (see
        //     `identityHeldRef`). Reopening the guard is the entire job.
        //   the sign-out THIS TAB is performing — `signOut()` notifies its listeners from
        //     inside the promise `signOutAsync` is awaiting (`_updateCurrentUser(null)`
        //     queues the notification before that `await` continues), so this runs BEFORE
        //     the cleanup that owns it, with `identityHeldRef` still raised. The in-flight
        //     promise is the truthful marker for it: whatever the two orderings do, the
        //     cleanup runs exactly once, in `signOutAsync`, where the navigation that
        //     belongs with it also lives. When the notification arrives late instead —
        //     after `signOutAsync` finished — the flight is over but the marker is already
        //     down, and it does not repeat either.
        if (identityHeldRef.current && !signOutInFlight.current) {
          clearLocalSessionRef.current();
        } else {
          sessionCookie.reset();
        }
        setIsInitializing(false);
      }
    });
    return () => {
      // BEFORE the release: the rejection that arrives between these two lines must
      // already see a disposed effect, or it re-arms the very hold being torn down.
      disposed = true;
      // A tenant switch re-runs this effect rather than unmounting the tree, so the two
      // markers say different things: `disposed` closes THIS closure, while the attempt
      // closes every read taken under the context being left — including one a callback
      // has already passed `disposed` with.
      mountedRef.current = false;
      identityAttempt.current += 1;
      // The notice's scope moves for a REAL move of the acting tenant and for nothing
      // else this cleanup can mean. `identityAttempt` above can afford to be blunt — it
      // only ever discards work — but the notice is the opposite: advancing it DROPS the
      // explanation the login screen is about to need. And this effect re-runs for more
      // than a tenant switch. Its dependency list also carries `tenantStatus` and
      // `mutateAsync`, both of which follow the react-query tenant observer. A re-render that
      // rebuilt the tenant query between a wrapped read starting and its rejection would
      // silently lose the notice. "Cleanup of the ended identity is not a new identity"
      // has to hold against the whole dependency list, not just against the tenant id.
      //
      // Disposal is the OTHER boundary that must advance, and it cannot be recognised
      // here — a re-run and an unmount reach this same line. It has an effect of its own,
      // below.
      const nextTenantId = tenantIdRef.current;
      if (effectTenantId && nextTenantId && effectTenantId !== nextTenantId) {
        beginSignOutReasonScope();
      }
      document.removeEventListener('visibilitychange', noteHidden);
      releaseHold?.();
      unsubscribe();
    };
  }, [
    assertAttempt,
    captureAttempt,
    isAttemptCurrent,
    mutateAsync,
    tenant?.id,
    tenantStatus,
    handleSetCurrentUser,
    shouldRetryDroppedRenewal,
  ]);

  /**
   * This owner going away for good.
   *
   * An empty dependency list, so it runs exactly once per real mount — which is the one
   * thing the listener effect's cleanup above cannot tell, because a tenant move and an
   * unmount arrive there as the same call. Nothing read under this provider may describe a
   * login screen rendered by whatever mounts next.
   */
  React.useEffect(() => () => beginSignOutReasonScope(), []);

  /**
   * Subscribes to query client and clears cache and logs out user
   * if either mutation or query returns unauthorized error.
   * Additionally, in case of other errors which are not based on forms, it shows a snackbar.
   */
  React.useEffect(() => {
    const unsubscribeMutations = queryClient
      .getMutationCache()
      .subscribe((sub) => {
        if (sub.type === 'updated' && sub.mutation.state.error) {
          if (
            shouldToastApiError(sub.mutation.state.error, {
              source: 'mutation',
            }) &&
            !isErrorToastSuppressed(sub.mutation)
          ) {
            enqueueSnackbar({
              message: translate(sub.mutation.state.error.message),
              variant: 'error',
            });
          }
          // A 401, the cookie's own deadline, or an identity the guard will not bind
          // (sen-5 F7). Not the renewable 403 — that one is answered by a renewal and a
          // replay.
          if (isSessionOverError(sub.mutation.state.error)) {
            // Logout and redirect
            if (queryClient) clearAccountCache(queryClient);
            signOutAsync({
              source: 'terminal',
              reason: getSignOutReason(sub.mutation.state.error),
            });
          }
        }
      });

    const unsubscribeQueries = queryClient.getQueryCache().subscribe((sub) => {
      if (sub.type === 'updated' && sub.query.state.error) {
        if (
          shouldToastApiError(sub.query.state.error, { source: 'query' }) &&
          !isQueryErrorToastSuppressed(sub.query, sub.action.type)
        ) {
          enqueueSnackbar({
            message: translate(sub.query.state.error.message),
            variant: 'error',
          });
        }
        if (isSessionOverError(sub.query.state.error)) {
          // Logout and redirect
          if (queryClient) clearAccountCache(queryClient);
          signOutAsync({
            source: 'terminal',
            reason: getSignOutReason(sub.query.state.error),
          });
        }
      }
    });
    // Both subscriptions leaked until now, and this effect DOES re-run: it depends on
    // `signOutAsync`, `translate` and `enqueueSnackbar`. Each extra listener is another
    // copy of every global error toast.
    return () => {
      unsubscribeMutations();
      unsubscribeQueries();
    };
  }, [enqueueSnackbar, formatMessage, queryClient, signOutAsync, translate]);
  /** Indicates that client onboarding is enforced and user can not switch to other type currently. */
  const isClientOnboardingRequired = React.useMemo(
    () =>
      Boolean(
        currentUser &&
        currentUser.status === UserStatusType.ACTIVE &&
        currentUser?.type === OrganizationUserType.CLIENT &&
        // BE falls back `type` to CLIENT for users with zero OrganizationUsers
        // rows (e.g. a `member` account still waiting for an org invite). Only
        // force the questionnaire when the user actually has an org
        // membership (invited-as-client flow) — otherwise a member-pending or
        // plain b2c account gets permanently redirect-trapped here.
        currentUser.organizationUsers &&
        currentUser.organizationUsers.length > 0 &&
        !currentUser?.fitnessProfile &&
        // TODO BE When we invite client from admin panel or org, it needs to have CLIENT role assigned beside of type, because on register he won't have any and he needs to be able to go back from flow
        currentUser.hasFinishedOnboarding,
      ),
    [currentUser],
  );

  const isPendingMemberInvite = React.useMemo(
    () => computeIsPendingMemberInvite(currentUser),
    [currentUser],
  );

  // Outstanding questionnaires are NOT decided here. What a pending
  // questionnaire does — lock the app, open a dismissible modal, or merely be
  // announced — depends on the organization currently selected, and that comes
  // from `OrganizationProvider`, which is mounted below this one. The split
  // lives in `useScopedPendingQuestionnaires`; this provider stays about
  // identity.

  const providedValue = React.useMemo(
    () => ({
      currentUser,
      hasEndedSession: endedSession.isActive,
      isAuthenticated: Boolean(currentUser?.id),
      isClientOnboardingRequired,
      isCredentialTransitionActive: credentialTransition.isActive,
      isLoading: isInitializing,
      isPendingMemberInvite,
      loginWithGoogleAsync,
      loginWithPasswordAsync,
      refetchCurrentUser,
      registerWithPasswordAsync,
      runCredentialTransition,
      sessionRevision,
      setCurrentUser: handleSetCurrentUser,
      signOutAsync,
    }),
    [
      isClientOnboardingRequired,
      isPendingMemberInvite,
      registerWithPasswordAsync,
      runCredentialTransition,
      credentialTransition.isActive,
      currentUser,
      endedSession.isActive,
      isInitializing,
      sessionRevision,
      handleSetCurrentUser,
      refetchCurrentUser,
      loginWithGoogleAsync,
      loginWithPasswordAsync,
      signOutAsync,
    ],
  );
  return <Context.Provider value={providedValue}>{children}</Context.Provider>;
};

export function useAuthContext() {
  return Context.useContext();
}
export default AuthProvider;
